import { useModal } from '@/features/Modal/Modal.hooks'
import { DrawerModalListItem } from '@/features/Modal/Modal.types'
import { Drawer, Flex, Spin } from 'antd'
import Link from 'next/link'
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import styles from './DrawerModalList.module.scss'
import CloseIcon from '@/assets/CloseIcon.svg'
import { MobileSkeletonLoader } from '@/components/atoms/MobileSkeletonLoader/MobileSkeletonLoader'
import { isReactNode } from '@/shared/helpers/react'
import { LoadingOutlined } from '@ant-design/icons'

type Props = {
  list: DrawerModalListItem[];
  isLoading?: boolean;
  size?: number;
  title?: string;
  description?: string | ReactNode;
}

export function DrawerModalList({ list, isLoading, title, description }: Props) {
  const { destroyModal } = useModal()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [drawerSize, setDrawerSize] = useState<number>(100)
  const [ready, setReady] = useState(false)

  const measure = useCallback(() => {
    const el = contentRef.current
    if (!el) return
    const extra = 24 + 24 + 30
    const max = Math.floor(window.innerHeight * 0.9)
    setDrawerSize(Math.min(max, el.scrollHeight + extra))
    setReady(true)
  }, [])

  return (
    <Drawer
      size={drawerSize}
      onClose={destroyModal}
      placement="bottom"
      title={null}
      footer={null}
      open
      afterOpenChange={(isOpen) => {
        if (!isOpen) {
          setDrawerSize(100)
          setReady(false)
          return
        }

        setDrawerSize(100)
        setReady(false)

        requestAnimationFrame(() => {
          requestAnimationFrame(measure)
        })
      }}
    >
      {!ready && <MobileSkeletonLoader height="22px" width="100%" amount={2} />}

      {ready && isLoading && (
        <div className={styles.loaderOverlay}>
          <LoadingOutlined spin />
        </div>
      )}

      <div ref={contentRef}
        style={{
          visibility: ready ? 'visible' : 'hidden',
          pointerEvents: ready ? 'auto' : 'none',
        }}>
        <span onClick={destroyModal} className={styles.close}>
          <CloseIcon />
        </span>

        {(title || description) && (
          <div className={`${styles.infoBlock} ${list.length === 0 && styles.infoBlockNoBorder}`}>
            {title && <p className={styles.title}>{title}</p>}
            {description && isReactNode(description) ? <div className={styles.description}>{description}</div> : <p className={styles.description}>{description}</p>}
          </div>
        )}

        <ul className={styles.list}>
          {list.map((item) => (
            item.visible && (
              <li key={item.text} className={item.isRemove && styles.remove}>
                {item.isLink ? (
                  <Link className={styles.listItem} onClick={destroyModal} href={item.link}>
                    {item.icon && <span className={styles.icon}>{item.icon}</span>}
                    {item.text}
                  </Link>
                ) : (
                  <span className={styles.listItem} onClick={item.onClick}>
                    {item.icon && <span className={styles.icon}>{item.icon}</span>}
                    {item.text}
                  </span>
                )}
              </li>
            )
          ))}
        </ul>
      </div>
    </Drawer>
  )
}
